
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionTierBadge } from '../components/SubscriptionTierBadge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import NotificationPermissionButton from '@/components/NotificationPermissionButton';
import TestNotificationButton from '@/components/TestNotificationButton';
import LanguageSelector from '@/components/LanguageSelector';
import PasswordChangeSection from '@/components/PasswordChangeSection';
import { NotificationSoundSettings } from '@/components/NotificationSoundSettings';
import StorageProgressBar from '@/components/StorageProgressBar';
import { QuotaDisplay } from '@/components/QuotaDisplay';
import { ReferralCodeCard } from '@/components/ReferralCodeCard';
import { RevenueCatDiagnostic } from '@/components/RevenueCatDiagnostic';
import { ArrowRight, Settings as SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Settings = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { subscription, quota, isAnyLoading } = useSubscription();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { t } = useTranslation();
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEasterEggTap = useCallback(() => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      setShowDiagnostic(true);
    } else {
      tapTimerRef.current = setTimeout(() => {
        tapCountRef.current = 0;
      }, 1500);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  const handleSignOut = async () => {
    setShowConfirmation(true);
  };

  const confirmSignOut = async () => {
    await signOut();
    setShowConfirmation(false);
    // The redirect will be handled by AuthContext's onAuthStateChange
  };

  const cancelSignOut = () => {
    setShowConfirmation(false);
  };

  if (!user) {
    return <div className="text-center mt-8">{t('common.loading')}</div>;
  }

  const currentTier = subscription?.data?.currentTier || "free";

  return (
    <div className="container mx-auto px-4 py-8 pb-20">
      <h1 className="text-3xl font-bold mb-6">{t('settings.title')}</h1>
      
      {/* User Profile Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('settings.userProfile')}</h2>
        <p><strong>{t('settings.userId')}:</strong> {user.id}</p>
        <p><strong>{t('settings.email')}:</strong> {user.email}</p>
        <Button variant="destructive" onClick={handleSignOut} className="mt-4">
          {t('common.signOut')}
        </Button>

        {showConfirmation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">{t('common.confirm')}</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">{t('settings.confirmSignOut')}</p>
                </div>
                <div className="items-center px-4 py-3">
                  <Button variant="ghost" className="mr-2" onClick={cancelSignOut}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="destructive" onClick={confirmSignOut}>
                    {t('common.signOut')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Password Change Section */}
      <div className="mb-8">
        <PasswordChangeSection />
      </div>

      {/* Language Settings Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('settings.languageSettings')}</h2>
        <p className="text-sm text-gray-500 mb-4">{t('settings.selectLanguage')}</p>
        <LanguageSelector />
      </div>

      {/* Notification Settings Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('settings.notificationSettings')}</h2>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t('settings.notifications.pushNotifications')}</h3>
              <p className="text-sm text-gray-500">
                {t('settings.notifications.pushDescription')}
              </p>
            </div>
            <NotificationPermissionButton />
          </div>
          
          <Separator className="my-2" />
          
          <div>
            <h3 className="font-medium mb-2">{t('settings.notifications.testNotifications')}</h3>
            <p className="text-sm text-gray-500 mb-3">
              {t('settings.notifications.testDescription')}
            </p>
            <TestNotificationButton />
          </div>
        </div>
      </div>

      {/* Sound Settings Section */}
      <div className="mb-8">
        <NotificationSoundSettings />
      </div>

      {/* Referral Section */}
      <div className="mb-8">
        <ReferralCodeCard />
      </div>
      
      {/* Subscription Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('settings.subscription')}</h2>
        {isAnyLoading ? (
          <p>{t('common.loading')}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t('settings.currentPlan')}:</p>
                <div className="mt-1">
                  <SubscriptionTierBadge tier={currentTier as "free" | "standard" | "premium" | "ultimate"} />
                </div>
              </div>
              <Button asChild>
                <Link to="/subscription" className="flex items-center">
                  {t('settings.manageSubscription')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            
            {/* ✅ FASE 3: Storage & Quota Display */}
            {quota && (
              <div className="space-y-3 mt-4">
                <StorageProgressBar
                  used={quota.storage.used}
                  total={quota.storage.total}
                  showUpgradeButton={true}
                />
                <QuotaDisplay
                  used={quota.pets.used}
                  total={quota.pets.total}
                  title={t('settings.pets', 'Pets')}
                />
              </div>
            )}
            
            <p className="text-sm text-gray-500">
              {t('settings.upgradeDescription')}
            </p>
          </div>
        )}
      </div>
      
      {/* Additional Settings Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('settings.additionalSettings')}</h2>
        <p>{t('settings.moreSettingsSoon')}</p>
        <div className="flex justify-center mt-4">
          <button
            onClick={handleEasterEggTap}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors select-none"
            aria-label="App info"
          >
            <SettingsIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Hidden RevenueCat Diagnostic Modal */}
      {showDiagnostic && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 relative">
            <button
              onClick={() => setShowDiagnostic(false)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
            <RevenueCatDiagnostic />
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
